import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Collapse, Paper, Skeleton, styled } from '@mui/material'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useCsvInbox } from 'hooks'
import {
   
    RTA_INBOX_TYPES,
} from 'utils'
import { isNilOrEmpty, useQuery } from 'packages/core'
import { Icon } from 'packages/eid-icons'
import { CollapsibleFilterPanel } from 'packages/eid-ui'
import { Overview } from './Overview/Overview'
import {
    Filters,
    FilterContainer,
    DesktopContainer,
    FiltersColumn,
    ListingGrid,
    GridContainer,
} from 'components'
import BreadcrumbsComponent from 'components/BreadCrumbs'
import useSubcomponents from 'useSubcomponents'
import { useAppState } from 'appContext'
import LandscapesFilter from 'components/Filters/LandscapesFilter'

// Type assertion for JS component
const Breadcrumbs = BreadcrumbsComponent as any

const smallScreenWidth = 960

interface FilterConfig {
    name: keyof typeof Filters.filtersMap
    config?: Record<string, unknown>
}

// Filter configuration map for different inbox types
const getFiltersByInboxType = (
    inboxTypeId: number | undefined,
    trackingID: string | undefined,
): FilterConfig[] => {
    const baseConfig = { trackingID }

    switch (inboxTypeId) {
        case RTA_INBOX_TYPES.GROUP_TO_BUSINESS_ROLE:
            return [
                {
                    name: 'groupSelectionFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/grouptobusinessrole/filters/groups',
                    },
                },
                {
                    name: 'orgRoleOrgZoneFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/grouptobusinessrole/filters/orgroleorgzones',
                    },
                },
                {
                    name: 'orgRoleFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/grouptobusinessrole/filters/orgroles',
                    },
                },
                {
                    name: 'orgZoneFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/grouptobusinessrole/filters/orgzones',
                    },
                },
                {
                    name: 'accountStoreFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/grouptobusinessrole/filters/accountstores',
                    },
                },
            ]

        case RTA_INBOX_TYPES.GROUP_ELIGIBILITY:
            return [
                {
                    name: 'groupSelectionFilter',
                    config: {
                        ...baseConfig,
                        url: 'api/RTA/groupeligibility/filters/applicationroles',
                    },
                },

                {
                    name: 'accountStoreFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/groupeligibility/filters/accountstores',
                    },
                },
                {
                    name: 'assigneeFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/groupeligibility/filters/assignees',
                    },
                },
            ]
        case RTA_INBOX_TYPES.DERIVED_BUSINESS_ROLE:
            return [
                {
                    name: 'orgRoleOrgZoneFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/derivedbusinessrole/filters/orgroleorgzones',
                    },
                },
                {
                    name: 'orgRoleFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/derivedbusinessrole/filters/orgroles',
                    },
                },
                {
                    name: 'orgZoneFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/derivedbusinessrole/filters/orgzones',
                    },
                },
            ]
        case RTA_INBOX_TYPES.TEMPLATE_BUSINESS_ROLE:
            return [
                {
                    name: 'accountStoreFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/templatebusinessrole/filters/accountstores',
                    },
                },
                {
                    name: 'orgRoleOrgZoneFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/templatebusinessrole/filters/orgroleorgzones',
                    },
                },
                {
                    name:'textFilter',
                    config: {
                        ...baseConfig,
                        label: 'HighLevelClassification',
                        contextProp: 'highLevelClassification',
                    },
                },
            ]
        case RTA_INBOX_TYPES.APPLICATION_ROLE_ATTRIBUTE:
            return [
                {
                    name: 'groupSelectionFilter',
                    config: {
                        ...baseConfig,
                        url: 'api/RTA/groupattributes/filters/applicationroles',
                    },
                },

                {
                    name: 'accountStoreFilter',
                    config: {
                        ...baseConfig,
                        url: '/api/RTA/groupattributes/filters/accountstores',
                    },
                },
                {
                    name:'textFilter',
                    config: {
                        ...baseConfig,
                        label: 'HighLevelClassification',
                        contextProp: 'highLevelClassification',
                    },
                },
            ]

        // Add more inbox types here as needed
        // case RTA_INBOX_TYPES.ANOTHER_TYPE:
        //     return [
        //         { name: 'someOtherFilter', config: baseConfig },
        //     ]

        default:
            // Default filters for unknown types or while loading
            return [
              
            ]
    }
}

export const CsvInboxDetail = (props: any) => {
    const filterContainer = useRef(null) as any
    const { hasAccessToControl } = useSubcomponents()
    const { t } = useTranslation()
    const { csvInboxId } = props
    const {
        data,
        refetch,
        isLoading: isCsvInboxLoading,
    } = useCsvInbox(csvInboxId)
    const query = useQuery()
    const [, dispatch]: any = useAppState()
    const history = useHistory()
    const [windowSize, setWindowSize] = useState(window.innerWidth)
    const [filterWidth, setFilterWidth] = useState(0)

    const [openFilters, setOpenFilters]: any = useState(true)
    const handleWindowResize = useCallback(() => {
        setWindowSize(window.innerWidth)
    }, [])

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize)

        return () => {
            window.removeEventListener('resize', handleWindowResize)
        }
    }, [handleWindowResize])

    useEffect(() => {
        setFilterWidth(filterContainer?.current?.offsetWidth)
    }, [openFilters, windowSize])

    // Get filters based on inbox type
    const filters: FilterConfig[] = getFiltersByInboxType(
        data?.csvInboxTypeID,
        data?.trackingID,
    )

    const allTabs = [
        {
            label: t('Overview'),
            value: 'overview',
            icon: <Icon name="Overview" />,
            display: true,
            default: true,
        },
    ]

  
    if (isCsvInboxLoading) {
        return (
            <Fragment>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
            </Fragment>
        )
    }

    // Function to navigate back to listing (remove id query param)
    const handleNavigateToListing = () => {
        const newQuery = new URLSearchParams(query.toString())
        newQuery.delete('id')
        history.push({
            pathname: history.location.pathname,
            search: newQuery.toString(),
        })
    }

    return (
        <>
            {/* Breadcrumbs for detail page - above main content */}
            <Box sx={{ paddingLeft: '8px', marginBottom: '16px' }}>
                {/* @ts-ignore - Breadcrumbs component is JS, types are inferred incorrectly */}
                <Breadcrumbs
                    breadcrumbs={[
                        {
                            labelKey: 'All',
                            onClick: handleNavigateToListing,
                        },
                        {
                            labelKey: 'CSVInbox',
                            disabled: true,
                        },
                        {
                            label: data?.fileName || t('Loading'),
                            loading: isCsvInboxLoading,
                            disableTruncation: true,
                        },
                    ]}
                />
            </Box>
            {/* Start of the filters section */}
            <DesktopContainer
                sx={{
                    paddingTop: '0rem',
                }}
            >
                <Fragment>
                    <FiltersColumn ref={filterContainer}>
                        <CollapsibleFilterPanel
                            openFilters={openFilters}
                            setOpenFilters={(e: any) =>
                                setOpenFilters((prev: any) => !prev)
                            }
                        />

                        <Collapse in={openFilters} timeout={300} unmountOnExit>
                            <>
                                {/* <NameContainer>{data?.fileName}</NameContainer> */}
                                <Box sx={{ marginBottom: '32px' }}>
                                    <LandscapesFilter
                                        trackingID={data?.trackingID}
                                    />
                                </Box>
                                {filters.map((f) => {
                                    const FilterToRender = Filters.filtersMap[
                                        f.name
                                    ] as React.ComponentType<{
                                        config?: Record<string, unknown>
                                    }>
                                    return (
                                        <FilterContainer key={f.name}>
                                            <FilterToRender config={f.config} />
                                        </FilterContainer>
                                    )
                                })}
                            </>
                        </Collapse>
                    </FiltersColumn>

                    <ListingGrid
                        sx={{
                            marginTop: '2.3rem',
                            minWidth: 0,
                        }}
                    >
                        <>
                            <GridContainer
                                filterWidth={filterWidth}
                                openFilters={openFilters}
                                // sx={{ minWidth: 0 }}
                            >
                                <Box
                                    sx={{
                                        borderRadius: '8px',
                                        minWidth: 0,
                                    }}
                                >
                                    <Overview csvInboxId={csvInboxId} />
                                </Box>
                            </GridContainer>
                        </>
                    </ListingGrid>
                </Fragment>
            </DesktopContainer>

            {/* End */}
        </>
    )
}

export const RootContainer = styled(Box)({
    position: 'relative',
    height: '100%',
    width: '100%',

    [`@media (max-width:${smallScreenWidth - 1}px)`]: {
        flexDirection: 'column',
    },
})

const ImageContainer = styled(Paper)({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
    minHeight: '125px',
    borderRadius: '8px',
    marginBottom: '20px',

    '& img': {
        maxHeight: '80px',
        maxWidth: '80px',
    },

    [`@media (max-width:${smallScreenWidth - 1}px)`]: {
        minWidth: '116px',
        '& img': {
            maxHeight: '33px',
            width: '66px',
        },
    },
})
const NameContainer = styled(Paper)({
    width: '100%',
    minHeight: '50px',
    marginBottom: '20px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    overflow: 'hidden',
    fontSize: '22px',
    fontWeight: 'bold',
    borderRadius: '8px',
    color: '#3b454d',
    wordBreak: 'break-word',
    padding: '5px',
})
export const TabsContainer = styled(Paper)({
    width: '100%',
    borderRadius: '0px',
    marginBottom: '20px',
})

export const TabsColumn = styled(Box)({
    display: 'flex',
    minWidth: '340px',
    flexDirection: 'column',
    margin: '0px 12px 0px 10px',
    marginRight: '20px',
    width: '20%',
    // flex: '30%',
})
